import React from 'react';
import { Dropdown } from './Dropdown';

export interface UserMenuItem {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
}

interface UserMenuProps {
  name?: string | null;
  role?: string | null;
  avatarUrl?: string | null;
  items: UserMenuItem[];
  /**
   * Display preferences (theme, language) rendered in their own section above sign-out.
   *
   * A slot rather than props, because these are inline controls with their own state and each app
   * holds its own i18n instance. They live here so the header can be brand + nav + avatar and
   * nothing else — five portals were putting two dropdowns, a name and a button in one
   * non-wrapping row, which does not fit a phone.
   */
  preferences?: React.ReactNode;
  onLogout: () => void;
  logoutLabel: string;
}

const initials = (name: string) =>
  name.split(' ').filter(Boolean).slice(0, 2).map((s) => s[0]).join('').toUpperCase() || '?';

const itemClass = 'flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-body-soft hover:bg-canvas';

/**
 * Header user control: avatar + name + role, opening a dropdown with custom
 * items (e.g. profile/settings) and a logout action. Shared across apps.
 */
export const UserMenu: React.FC<UserMenuProps> = ({ name, role, avatarUrl, items, preferences, onLogout, logoutLabel }) => (
  <Dropdown
    aria-label={name ?? 'Account'}
    triggerClassName="flex items-center gap-2 rounded-md px-1 py-1 hover:bg-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
    panelClassName="w-56"
    trigger={
      <>
        {avatarUrl ? (
          <img src={avatarUrl} alt="" className="h-8 w-8 rounded-full object-cover" />
        ) : (
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-ink-strong">
            {initials(name ?? '')}
          </span>
        )}
        <span className="hidden flex-col leading-tight text-right sm:flex">
          {name && <span className="text-sm font-medium text-body-soft">{name}</span>}
          {role && <span className="text-xs text-subtle-ink">{role}</span>}
        </span>
      </>
    }
  >
    <div className="border-b border-line-soft px-3 py-2">
      {name && <p className="truncate text-sm font-medium text-body">{name}</p>}
      {role && <p className="text-xs text-subtle-ink">{role}</p>}
    </div>
    {items.map((item) => (
      <button key={item.label} type="button" onClick={item.onClick} className={itemClass} role="menuitem">
        {item.icon}
        {item.label}
      </button>
    ))}
    {preferences && (
      <>
        <div className="my-1 border-t border-line-soft" />
        {preferences}
      </>
    )}
    <div className="my-1 border-t border-line-soft" />
    <button type="button" onClick={onLogout} className={`${itemClass} text-red-600 hover:bg-red-50`} role="menuitem">
      {logoutLabel}
    </button>
  </Dropdown>
);
